import { PageController } from '@client/components/_core/layout/PageController';
import type { ScopeValue } from '@client/components/pds-compare/CategoryScopeFilters';
import { PdsComparePage } from '@client/components/pds-compare/PdsComparePage';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

const pdsCompareSearchSchema = z.object({
    category: z.string().optional(),
    scope: z.string().optional(),
    insurers: z.string().optional(), // comma-separated insurer names
});

export type PdsCompareSearch = z.infer<typeof pdsCompareSearchSchema>;

export const Route = createFileRoute('/console/$orgId/tools/pds-compare/')({
    staticData: {
        ui: {
            widthVariant: 'fullWidth',
        },
        modules: ['pds-compare'],
    },
    validateSearch: zodValidator(pdsCompareSearchSchema),
    component: PdsCompare,
});

/** Normalise legacy scope values from old bookmarked URLs */
const LEGACY_SCOPE_MAP: Record<string, ScopeValue> = {
    FINANCIAL_LIMITS: 'LIMIT',
    DEFINITIONS_LIST: 'DEFINITION',
};

function PdsCompare() {
    const search = Route.useSearch();
    const navigate = Route.useNavigate();

    const initialCategory = search.category ?? null;
    const rawScope = (search.scope as string) ?? 'SUMMARY';
    const initialScope = (LEGACY_SCOPE_MAP[rawScope] ?? rawScope) as ScopeValue;
    const initialInsurers = search.insurers ? search.insurers.split(',').filter(Boolean) : [];

    const handleSearchUpdate = (params: { category?: string | null; scope?: string | null; insurers?: string[] }) => {
        navigate({
            search: (prev: PdsCompareSearch) => ({
                ...prev,
                ...(params.category !== undefined ? { category: params.category ?? undefined } : {}),
                ...(params.scope !== undefined ? { scope: params.scope ?? undefined } : {}),
                ...(params.insurers !== undefined
                    ? { insurers: params.insurers.length > 0 ? params.insurers.join(',') : undefined }
                    : {}),
            }),
            replace: true,
        });
    };

    return (
        <PageController route={Route}>
            <PdsComparePage
                initialCategory={initialCategory}
                initialScope={initialScope}
                initialInsurers={initialInsurers}
                onSearchUpdate={handleSearchUpdate}
            />
        </PageController>
    );
}
